import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  IconButton,
  Stack,
  InputAdornment,
  useTheme,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  PostAdd as PostAddIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditJobIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { getAuthUser } from '../../services/authService';
import { parseResume } from '../../services/resumeParserService';
import { apiPost } from '../../lib/apiClient';
import { supabase } from '../../lib/supabaseClient';
import { Breadcrumbs, FormSkeleton } from '../../components/ui';

interface JobFormData {
  title: string;
  description: string;
  location: string;
  isRemote: boolean;
  salaryMin: string;
  salaryMax: string;
  salaryCurrency: string;
  requirements: string[];
  benefits: string[];
  expiryDate: string;
  isPublic: boolean;
  status: string;
}

export const PostJobPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: jobId } = useParams<{ id: string }>();
  const theme = useTheme();
  const isEditMode = !!jobId;

  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    description: '',
    location: '',
    isRemote: false,
    salaryMin: '',
    salaryMax: '',
    salaryCurrency: 'AED',
    requirements: [],
    benefits: [],
    expiryDate: '',
    isPublic: true,
    status: 'active'
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loadingJob, setLoadingJob] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [parsing, setParsing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isApprovedJob, setIsApprovedJob] = useState(false); // Track if job is approved (read-only)
  const [jobAudit, setJobAudit] = useState<{
    createdAt: string | null;
    createdByName: string | null;
    updatedAt: string | null;
    approvedAt: string | null;
    approvedByName: string | null;
    status: string | null;
  }>({ createdAt: null, createdByName: null, updatedAt: null, approvedAt: null, approvedByName: null, status: null });

  // Load job data if in edit mode
  useEffect(() => {
    const loadData = async () => {
      const user = await getAuthUser();
      setCurrentUser(user);

      if (jobId) {
        await loadJobData();
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const loadJobData = async () => {
    try {
      setLoadingJob(true);
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select(`
          *,
          creator:users!created_by(name),
          approver:users!approved_by(name)
        `)
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;

      if (job) {
        setFormData({
          title: job.title || '',
          description: job.description || '',
          location: job.location || '',
          isRemote: job.is_remote || false,
          salaryMin: job.salary_min?.toString() || '',
          salaryMax: job.salary_max?.toString() || '',
          salaryCurrency: job.salary_currency || 'AED',
          requirements: job.requirements?.length > 0 ? job.requirements : [''],
          benefits: job.benefits?.length > 0 ? job.benefits : [''],
          expiryDate: job.expiry_date || '',
          isPublic: job.is_public ?? true,
          status: job.status || 'active'
        });

        // Mark as approved/read-only if status is active
        setIsApprovedJob(job.status === 'active');

        // Populate audit data
        setJobAudit({
          createdAt: job.created_at,
          createdByName: job.creator?.name || null,
          updatedAt: job.updated_at,
          approvedAt: job.approved_at || null,
          approvedByName: job.approver?.name || null,
          status: job.status,
        });
      }
    } catch (err: any) {
      console.error('Error loading job:', err);
      setError('Failed to load job data');
    } finally {
      setLoadingJob(false);
    }
  };

  // AI Generation Dialog State
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiInputs, setAiInputs] = useState({
    jobTitle: '',
    keySkills: '',
    experienceLevel: 'Mid',
    companyDescription: '',
    location: '',
    isRemote: false,
    salaryRange: ''
  });

  const handleInputChange = (field: keyof JobFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAiInputChange = (field: string, value: any) => {
    setAiInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenAiDialog = () => {
    setAiDialogOpen(true);
    setError('');
  };

  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
    setAiInputs({
      jobTitle: '',
      keySkills: '',
      experienceLevel: 'Mid',
      companyDescription: '',
      location: '',
      isRemote: false,
      salaryRange: ''
    });
  };

  const handleGenerateWithAI = async () => {
    if (!aiInputs.jobTitle.trim()) {
      setError('Please enter a job title to generate the description');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const result = await apiPost('/api/generate-job-description', aiInputs);

      if (result.success && result.data) {
        const generatedJD = result.data;

        // Auto-fill form with AI-generated data
        setFormData({
          title: generatedJD.title || '',
          description: generatedJD.description || '',
          location: generatedJD.location || aiInputs.location || '',
          isRemote: generatedJD.isRemote || aiInputs.isRemote,
          salaryMin: generatedJD.salaryMin ? generatedJD.salaryMin.toString() : '',
          salaryMax: generatedJD.salaryMax ? generatedJD.salaryMax.toString() : '',
          salaryCurrency: generatedJD.salaryCurrency || 'AED',
          requirements: generatedJD.requirements && generatedJD.requirements.length > 0
            ? generatedJD.requirements
            : [''],
          benefits: generatedJD.benefits && generatedJD.benefits.length > 0
            ? generatedJD.benefits
            : [''],
          expiryDate: generatedJD.expiryDate || '',
          isPublic: true,
          status: 'active'
        });

        setSuccess('Job description generated successfully with AI! Review and edit the details below.');
        handleCloseAiDialog();
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error: any) {
      console.error('Error generating job description with AI:', error);
      setError(error.message || 'Failed to generate job description. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const parseJobDescription = async (file: File) => {
    setParsing(true);
    setError('');
    setUploadedFile(file);

    try {
      // Extract text from the file
      const parsed = await parseResume(file);
      const jobDescriptionText = parsed.extractedText;

      // Send to backend for AI parsing
      const result = await apiPost('/api/parse-job-description', { jobDescriptionText });

      if (result.success && result.data) {
        const parsedJD = result.data;

        // Auto-fill form with parsed data
        setFormData({
          title: parsedJD.title || '',
          description: parsedJD.description || '',
          location: parsedJD.location || '',
          isRemote: parsedJD.isRemote || false,
          salaryMin: parsedJD.salaryMin ? parsedJD.salaryMin.toString() : '',
          salaryMax: parsedJD.salaryMax ? parsedJD.salaryMax.toString() : '',
          salaryCurrency: parsedJD.salaryCurrency || 'AED',
          requirements: parsedJD.requirements && parsedJD.requirements.length > 0
            ? parsedJD.requirements
            : [''],
          benefits: parsedJD.benefits && parsedJD.benefits.length > 0
            ? parsedJD.benefits
            : [''],
          expiryDate: parsedJD.expiryDate || '',
          isPublic: true,
          status: 'active'
        });

        setSuccess('Job description parsed successfully! Review and edit the details below.');
      } else {
        throw new Error('Invalid response from backend');
      }
    } catch (error: any) {
      console.error('Error parsing job description:', error);
      setError(error.message || 'Failed to parse job description. Please try again or enter manually.');
    } finally {
      setParsing(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      await parseJobDescription(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    multiple: false,
    disabled: parsing || loading
  });

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setError('Job title is required');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Job description is required');
      return false;
    }
    if (!formData.location.trim()) {
      setError('Location is required');
      return false;
    }
    if (!formData.expiryDate) {
      setError('Expiry date is required');
      return false;
    }
    if (formData.salaryMin && formData.salaryMax) {
      if (parseInt(formData.salaryMin) > parseInt(formData.salaryMax)) {
        setError('Minimum salary cannot be greater than maximum salary');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (isDraft: boolean) => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const user = currentUser || await getAuthUser();

      if (!user) {
        setError('You must be logged in to post a job');
        setLoading(false);
        return;
      }

      // Requirements and benefits are no longer collected from the form
      const filteredRequirements: string[] = [];
      const filteredBenefits: string[] = [];

      // Roles that can directly publish jobs (without approval)
      const canPublishDirectly = ['admin', 'hr', 'manager'].includes(user.role || '');

      if (isEditMode && jobId) {
        // Update existing job
        const updateData = {
          title: formData.title,
          description: formData.description,
          location: formData.location,
          is_remote: formData.isRemote,
          salary_min: formData.salaryMin ? parseInt(formData.salaryMin) : null,
          salary_max: formData.salaryMax ? parseInt(formData.salaryMax) : null,
          salary_currency: formData.salaryCurrency,
          requirements: filteredRequirements,
          benefits: filteredBenefits,
          expiry_date: formData.expiryDate,
          is_public: formData.isPublic,
          status: isDraft ? 'draft' : formData.status,
          updated_at: new Date().toISOString()
        };

        // If status changed to active by HR/Admin, set approval info
        if (formData.status === 'active' && canPublishDirectly) {
          Object.assign(updateData, {
            approved_by: user.id,
            approved_at: new Date().toISOString()
          });
        }

        const { error: updateError } = await supabase
          .from('jobs')
          .update(updateData)
          .eq('id', jobId);

        if (updateError) throw updateError;

        setSuccess('Job updated successfully!');
      } else {
        // Create new job
        const jobData = {
          title: formData.title,
          description: formData.description,
          location: formData.location,
          is_remote: formData.isRemote,
          salary_min: formData.salaryMin ? parseInt(formData.salaryMin) : null,
          salary_max: formData.salaryMax ? parseInt(formData.salaryMax) : null,
          salary_currency: formData.salaryCurrency,
          requirements: filteredRequirements,
          benefits: filteredBenefits,
          expiry_date: formData.expiryDate,
          is_public: formData.isPublic,
          status: isDraft ? 'draft' : (canPublishDirectly ? 'active' : 'pending'),
          entity_id: user.entityId,
          created_by: user.id,
          approved_by: canPublishDirectly && !isDraft ? user.id : null,
          approved_at: canPublishDirectly && !isDraft ? new Date().toISOString() : null
        };

        // Create job via API
        await apiPost('/api/create-job', jobData);

        setSuccess(
          isDraft
            ? 'Job saved as draft successfully!'
            : user.role === 'recruiter'
            ? 'Job submitted for approval! HR will review it shortly.'
            : 'Job posted successfully!'
        );
      }

      setSubmitted(true);

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/admin/jobs');
      }, 2000);

    } catch (err: any) {
      console.error('Error posting job:', err);
      setError(err.message || 'Failed to save job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Jobs', href: '/admin/jobs' },
            { label: isEditMode ? (formData.title || 'Edit Job') : 'Post New Job' }
          ]}
        />

        {/* Loading State for Edit Mode */}
        {loadingJob && (
          <FormSkeleton fields={8} />
        )}

        {!loadingJob && (
          <>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 4
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <WorkIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {isApprovedJob ? 'View Job (Read Only)' : isEditMode ? 'Edit Job' : 'Post a New Job'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                {isApprovedJob
                  ? 'This job has been approved and cannot be edited'
                  : isEditMode
                  ? 'Update job details and status'
                  : 'Create a job posting to attract top talent'}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Read-only warning for approved jobs */}
        {isApprovedJob && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
            This job has been approved and is now public. Approved jobs cannot be edited. You can only delete this job if needed.
          </Alert>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* AI Job Description Tools - Hidden for approved jobs */}
        {!isApprovedJob && (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              AI-Powered Job Description Tools
            </Typography>
            <Chip label="NEW" size="small" color="primary" />
          </Stack>

          <Grid container spacing={3}>
            {/* Generate from Scratch */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AutoAwesomeIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Generate from Scratch
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Create a complete job description using AI. Just provide a job title and optional details, and let AI generate a comprehensive JD for you.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleOpenAiDialog}
                    disabled={parsing || loading || generating}
                    fullWidth
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    Generate with AI
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            {/* Parse from Document */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CloudUploadIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h6" fontWeight={600}>
                      Parse from Document
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Upload an existing job description document (PDF, DOC, DOCX, or TXT) and let AI extract and fill in the details automatically.
                  </Typography>

                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${
                        isDragActive ? theme.palette.primary.main : theme.palette.divider
                      }`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: parsing ? 'not-allowed' : 'pointer',
                      bgcolor: isDragActive
                        ? theme.palette.action.hover
                        : 'transparent',
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: parsing ? 'transparent' : theme.palette.action.hover,
                        borderColor: parsing ? theme.palette.divider : theme.palette.primary.main
                      }
                    }}
                  >
                    <input {...getInputProps()} />
                    <CloudUploadIcon
                      sx={{ fontSize: 40, color: 'primary.main', mb: 1, opacity: parsing ? 0.5 : 1 }}
                    />
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {parsing
                        ? 'Parsing...'
                        : isDragActive
                        ? 'Drop here'
                        : 'Drop file or click'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {parsing ? 'Please wait...' : 'PDF, DOC, DOCX, TXT'}
                    </Typography>
                    {uploadedFile && !parsing && (
                      <Chip
                        label={uploadedFile.name}
                        color="primary"
                        size="small"
                        sx={{ mt: 1 }}
                        onDelete={() => setUploadedFile(null)}
                      />
                    )}
                  </Box>

                  {parsing && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="caption" color="text.secondary">
                        Analyzing with AI...
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
        )}

        {/* Form */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
          <Grid container spacing={3}>
            {/* Job Title */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                placeholder="e.g., Senior Software Engineer"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
                disabled={isApprovedJob}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon color="action" />
                    </InputAdornment>
                  ),
                  readOnly: isApprovedJob
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Job Description"
                placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
                disabled={isApprovedJob}
                InputProps={{ readOnly: isApprovedJob }}
              />
            </Grid>

            {/* Location & Remote */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Location"
                placeholder="e.g., San Francisco, CA"
                value={formData.location}
                disabled={isApprovedJob}
                onChange={(e) => handleInputChange('location', e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRemote}
                    onChange={(e) => handleInputChange('isRemote', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>Remote Position</Typography>
                    {formData.isRemote && <Chip label="Remote" size="small" color="primary" />}
                  </Box>
                }
              />
            </Grid>

            {/* Compensation */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main, mt: 2 }}>
                Compensation
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Salary"
                placeholder="80000"
                value={formData.salaryMin}
                onChange={(e) => handleInputChange('salaryMin', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                     
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Salary"
                placeholder="120000"
                value={formData.salaryMax}
                onChange={(e) => handleInputChange('salaryMax', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                   
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Currency"
                value={formData.salaryCurrency}
                onChange={(e) => handleInputChange('salaryCurrency', e.target.value)}
              >
                <MenuItem value="AED">AED (د.إ)</MenuItem>
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="EUR">EUR (€)</MenuItem>
                <MenuItem value="GBP">GBP (£)</MenuItem>
                <MenuItem value="INR">INR (₹)</MenuItem>
              </TextField>
            </Grid>

            {/* Additional Settings */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main, mt: 2 }}>
                Additional Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={formData.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon color="action" />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0]
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={500}>Public Job Posting</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.isPublic ? 'Visible on public job board' : 'Internal only'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Status Field - Visible in Edit Mode */}
            {/* HR/Admin/Manager can change to any status */}
            {isEditMode && currentUser && ['admin', 'hr', 'manager'].includes(currentUser.role || '') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Job Status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  helperText="Change the status of this job posting"
                >
                  <MenuItem value="active">Approved - Published and accepting applications</MenuItem>
                  <MenuItem value="pending">Pending - Awaiting approval</MenuItem>
                  <MenuItem value="draft">Draft - Not published</MenuItem>
                  <MenuItem value="closed">Closed - No longer accepting applications</MenuItem>
                </TextField>
              </Grid>
            )}

            {/* Recruiters can only edit draft/pending jobs and cannot change to active */}
            {isEditMode && currentUser && currentUser.role === 'recruiter' && ['draft', 'pending'].includes(formData.status) && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Job Status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  helperText="Save as draft or submit for HR approval"
                >
                  <MenuItem value="draft">Draft - Save for later editing</MenuItem>
                  <MenuItem value="pending">Pending - Submit for HR approval</MenuItem>
                </TextField>
              </Grid>
            )}

            {/* Audit Trail - Only in edit mode */}
            {isEditMode && jobAudit.createdAt && (
              <Grid item xs={12}>
                <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <HistoryIcon color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Audit Trail
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ position: 'relative', pl: 3 }}>
                      {/* Vertical timeline line */}
                      <Box sx={{
                        position: 'absolute',
                        left: 10,
                        top: 8,
                        bottom: 8,
                        width: 2,
                        bgcolor: 'divider',
                      }} />

                      {/* Job Created */}
                      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5, position: 'relative' }}>
                        <Box sx={{
                          position: 'absolute',
                          left: -19,
                          top: 4,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <PostAddIcon sx={{ fontSize: 12, color: '#fff' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Job Created
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(jobAudit.createdAt).toLocaleString()}
                          </Typography>
                          {jobAudit.createdByName && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              By: {jobAudit.createdByName}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {/* Job Approved */}
                      {jobAudit.approvedAt && (
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5, position: 'relative' }}>
                          <Box sx={{
                            position: 'absolute',
                            left: -19,
                            top: 4,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <CheckCircleIcon sx={{ fontSize: 12, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Job Approved
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(jobAudit.approvedAt).toLocaleString()}
                            </Typography>
                            {jobAudit.approvedByName && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                By: {jobAudit.approvedByName}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      )}

                      {/* Last Updated */}
                      {jobAudit.updatedAt && jobAudit.updatedAt !== jobAudit.createdAt && (
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5, position: 'relative' }}>
                          <Box sx={{
                            position: 'absolute',
                            left: -19,
                            top: 4,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'info.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <EditJobIcon sx={{ fontSize: 12, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Last Updated
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(jobAudit.updatedAt).toLocaleString()}
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                      {/* Current Status */}
                      {jobAudit.status && (
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ position: 'relative' }}>
                          <Box sx={{
                            position: 'absolute',
                            left: -19,
                            top: 4,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: jobAudit.status === 'active' ? 'success.main' : jobAudit.status === 'closed' ? 'error.main' : 'warning.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <SwapHorizIcon sx={{ fontSize: 12, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Current Status: {jobAudit.status.charAt(0).toUpperCase() + jobAudit.status.slice(1)}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/jobs')}
                  disabled={loading}
                  size="large"
                >
                  {isApprovedJob ? 'Back to Jobs' : 'Cancel'}
                </Button>
                {!isEditMode && !isApprovedJob && (
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSubmit(true)}
                    disabled={loading || submitted}
                    size="large"
                  >
                    Save as Draft
                  </Button>
                )}
                {!isApprovedJob && (
                <Button
                  variant="contained"
                  startIcon={isEditMode ? <SaveIcon /> : <SendIcon />}
                  onClick={() => handleSubmit(false)}
                  disabled={loading || submitted}
                  size="large"
                  sx={{ px: 4 }}
                >
                  {loading ? 'Saving...' : submitted ? 'Submitted' : isEditMode ? 'Update Job' : 'Post Job'}
                </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
        </>
        )}

        {/* AI Generation Dialog */}
        <Dialog
          open={aiDialogOpen}
          onClose={handleCloseAiDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4 }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <AutoAwesomeIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Generate Job Description with AI
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Provide basic details and let AI create a comprehensive job description
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleCloseAiDialog} disabled={generating}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Job Title - Required */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Job Title"
                  placeholder="e.g., Senior Frontend Developer"
                  value={aiInputs.jobTitle}
                  onChange={(e) => handleAiInputChange('jobTitle', e.target.value)}
                  disabled={generating}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WorkIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  helperText="Required - The position you're hiring for"
                />
              </Grid>

              {/* Key Skills */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Key Skills & Requirements"
                  placeholder="e.g., React, TypeScript, 5+ years experience, team leadership"
                  value={aiInputs.keySkills}
                  onChange={(e) => handleAiInputChange('keySkills', e.target.value)}
                  disabled={generating}
                  helperText="Optional - Main skills and qualifications needed"
                />
              </Grid>

              {/* Experience Level */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Experience Level"
                  value={aiInputs.experienceLevel}
                  onChange={(e) => handleAiInputChange('experienceLevel', e.target.value)}
                  disabled={generating}
                  helperText="Select the seniority level"
                >
                  <MenuItem value="Entry">Entry Level (0-2 years)</MenuItem>
                  <MenuItem value="Mid">Mid Level (3-5 years)</MenuItem>
                  <MenuItem value="Senior">Senior Level (6-9 years)</MenuItem>
                  <MenuItem value="Lead">Lead / Principal (10-14 years)</MenuItem>
                  <MenuItem value="Executive">Executive / C-Level (15+ years)</MenuItem>
                </TextField>
              </Grid>

              {/* Location */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  placeholder="e.g., San Francisco, CA"
                  value={aiInputs.location}
                  onChange={(e) => handleAiInputChange('location', e.target.value)}
                  disabled={generating}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  helperText="Optional - Office location"
                />
              </Grid>

              {/* Remote Option */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiInputs.isRemote}
                      onChange={(e) => handleAiInputChange('isRemote', e.target.checked)}
                      disabled={generating}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={500}>Remote Position</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Check if this is a remote role
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Salary Range */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Salary Range"
                  placeholder="e.g. 10000-15000"
                  value={aiInputs.salaryRange}
                  onChange={(e) => handleAiInputChange('salaryRange', e.target.value)}
                  disabled={generating}
                  InputProps={{
                    
                  }}
                  helperText="Optional - Expected salary range"
                />
              </Grid>

              {/* Company Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Company Description"
                  placeholder="Brief description of your company, culture, or what makes it unique..."
                  value={aiInputs.companyDescription}
                  onChange={(e) => handleAiInputChange('companyDescription', e.target.value)}
                  disabled={generating}
                  helperText="Optional - Company context to include in the description"
                />
              </Grid>
            </Grid>

            {generating && (
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CircularProgress size={24} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      AI is generating your job description...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This may take 10-20 seconds
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleCloseAiDialog}
              disabled={generating}
              size="large"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerateWithAI}
              disabled={generating || !aiInputs.jobTitle.trim()}
              size="large"
              sx={{ px: 4 }}
            >
              {generating ? 'Generating...' : 'Generate Job Description'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};
